// src/services/EventSystem.ts
// Central event bus — every significant action emits an event.
// Powers timeline, AI memory, notifications, analytics.

import { supabase } from '@/lib/supabase';
import { JourneyEvent, JourneyEventType, JourneyEventSource } from '@/types/Journey.types';

interface EmitOptions {
  eventType: JourneyEventType;
  payload: Record<string, any>;
  source: JourneyEventSource;
  relatedId?: string;
}

export class EventSystem {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  async emit(options: EmitOptions): Promise<JourneyEvent | null> {
    const { eventType, payload, source, relatedId } = options;

    try {
      const { data, error } = await supabase
        .from('journey_events')
        .insert({
          user_id: this.userId,
          event_type: eventType,
          payload,
          source,
          related_id: relatedId ?? null,
        })
        .select()
        .single();

      if (error) {
        console.error('EventSystem.emit error:', error);
        return null;
      }

      return this.transform(data);
    } catch (err) {
      console.error('EventSystem.emit unexpected error:', err);
      return null;
    }
  }

  async getTimeline(options?: {
    startDate?: string;
    endDate?: string;
    eventTypes?: JourneyEventType[];
    limit?: number;
    offset?: number;
  }): Promise<JourneyEvent[]> {
    try {
      let query = supabase
        .from('journey_events')
        .select('*')
        .eq('user_id', this.userId)
        .order('created_at', { ascending: false });

      if (options?.startDate) query = query.gte('created_at', options.startDate);
      if (options?.endDate) query = query.lte('created_at', options.endDate);
      if (options?.eventTypes?.length) query = query.in('event_type', options.eventTypes);
      if (options?.limit) query = query.limit(options.limit);
      if (options?.offset && options?.limit) {
        query = query.range(options.offset, options.offset + options.limit - 1);
      }

      const { data, error } = await query;
      if (error) {
        console.error('EventSystem.getTimeline error:', error);
        return [];
      }

      return (data ?? []).map(this.transform);
    } catch (err) {
      console.error('EventSystem.getTimeline unexpected error:', err);
      return [];
    }
  }

  async getEventsByRelatedId(relatedId: string): Promise<JourneyEvent[]> {
    try {
      const { data, error } = await supabase
        .from('journey_events')
        .select('*')
        .eq('user_id', this.userId)
        .eq('related_id', relatedId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('EventSystem.getEventsByRelatedId error:', error);
        return [];
      }

      return (data ?? []).map(this.transform);
    } catch (err) {
      return [];
    }
  }

  private transform(row: any): JourneyEvent {
    return {
      id: row.id,
      userId: row.user_id,
      eventType: row.event_type,
      payload: row.payload ?? {},
      source: row.source,
      relatedId: row.related_id ?? undefined,
      createdAt: row.created_at,
    };
  }
}
